"use client";

import React from "react";
import { Upload, CircleCheck, CircleAlert, Loader, Printer, Scissors, FileText } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { RangeInput } from "@/components/ui/range-input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
const inputBaseStyles = "flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors";
const inputStyles = cn(inputBaseStyles, "h-10");
const textareaStyles = cn(inputBaseStyles, "min-h-[120px] resize-y");
const productOptions = ["First-Time Buyer", "Remortgage", "Buy to Let", "Home Move", "Equity Release", "Commercial Loan", "Other"];
function QuoteFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-12 bg-card border border-border rounded-md shadow-2xl text-center max-w-2xl mx-auto relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-primary via-secondary to-primary" />
        <CircleCheck className="w-16 h-16 text-secondary mx-auto mb-6" />
        <h3 className="text-3xl font-default font-bold text-foreground mb-4">Application Submitted</h3>
        <p className="text-muted-foreground text-lg mb-8">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" size="lg" className="font-semibold rounded-md">
          Start New Application
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8 md:space-y-0 relative max-w-4xl mx-auto">
      
      {}
      <div className="bg-card p-6 md:p-8 rounded-md shadow-xl border border-border border-t-4 border-t-primary mx-auto max-w-md">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-primary/10 p-2 rounded-md text-primary">
            <FileText className="w-5 h-5" />
          </div>
          <h3 className="font-default text-2xl font-semibold text-foreground">1. Personal Information</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="name" rules={{
          required: "Full name is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default">Full Name</FormLabel>
                <FormControl>
                  <input type="text" placeholder="Alexander Sterling" className={inputStyles} {...field} />
                </FormControl>
                <FormDescription className="text-xs">Enter your legal name as it appears on your ID.</FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Invalid email address"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default">Email Address</FormLabel>
                <FormControl>
                  <input type="email" placeholder="alex@premierloans.com" className={inputStyles} {...field} />
                </FormControl>
                <FormDescription className="text-xs">We will send your pre-approval status here.</FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="phone" rules={{
          pattern: {
            value: /^[\d\s\+\-\(\)]+$/,
            message: "Invalid phone number format"
          }
        }} render={({
          field
        }) => <FormItem className="md:col-span-2">
                <FormLabel className="font-default">Contact Number <span className="text-muted-foreground font-normal">(Optional)</span></FormLabel>
                <FormControl>
                  <input type="tel" placeholder="+44 20 7946 0000" className={inputStyles} {...field} />
                </FormControl>
                <FormDescription className="text-xs">A mortgage advisor may call to discuss your options.</FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      {}
      <div className="bg-card p-6 md:p-8 rounded-md shadow-2xl border border-border border-t-4 border-t-secondary mx-auto max-w-md">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-secondary/10 p-2 rounded-md text-secondary">
            <Scissors className="w-5 h-5" />
          </div>
          <h3 className="font-default text-2xl font-semibold text-foreground">2. Loan Requirements</h3>
        </div>

        <div className="space-y-8">
          <FormField control={control} name="product_type" rules={{
          required: "Please select a loan purpose"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-base font-default">Purpose of Loan</FormLabel>
                <FormDescription className="mb-4 text-xs">Select the category that best describes your mortgage needs.</FormDescription>
                <FormControl>
                  <RadioGroup onValueChange={field.onChange} defaultValue={field.value} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {productOptions.map((option, i) => <FormItem key={option} data-index={i} className="flex items-center space-x-3 space-y-0 bg-muted/30 p-3 rounded-md border border-transparent hover:border-secondary/30 transition-colors">
                        <FormControl>
                          <RadioGroupItem value={option} />
                        </FormControl>
                        <FormLabel className="font-medium cursor-pointer w-full text-sm">{option}</FormLabel>
                      </FormItem>)}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="quantity" rules={{
          required: "Credit score estimate is required",
          min: {
            value: 300,
            message: "Minimum score is 300"
          }
        }} render={({
          field
        }) => <FormItem className="bg-muted/20 p-6 rounded-md border border-border/50">
                <div className="flex justify-between items-end mb-4">
                  <div>
                    <FormLabel className="text-base font-default">Estimated Credit Score</FormLabel>
                    <FormDescription className="text-xs">Provide your best estimate of your current credit rating.</FormDescription>
                  </div>
                  <div className="text-3xl font-default font-bold text-secondary">
                    {field.value || 720}
                  </div>
                </div>
                <FormControl>
                  <RangeInput min={300} max={850} step={1} value={field.value || 720} onValueChange={field.onChange} className="py-4" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="description" rules={{
          required: "Please provide financial context"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default">Financial Overview</FormLabel>
                <FormControl>
                  <textarea placeholder="Please describe your employment status, annual income, and any existing debts." className={textareaStyles} {...field} />
                </FormControl>
                <FormDescription className="text-xs">Detailed information helps us find the most competitive rates for you.</FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      {}
      <div className="bg-card p-6 md:p-8 rounded-md shadow-3xl border border-border border-t-4 border-t-primary/50 mx-auto max-w-md">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-primary/10 p-2 rounded-md text-primary">
            <Printer className="w-5 h-5" />
          </div>
          <h3 className="font-default text-2xl font-semibold text-foreground">3. Supporting Documents</h3>
        </div>

        <FormField control={control} name="artwork" render={({
        field: {
          value,
          onChange,
          ...field
        }
      }) => <FormItem>
              <FormLabel className="font-default">Upload Proof of Income <span className="text-muted-foreground font-normal">(Optional)</span></FormLabel>
              <FormControl>
                <div className="border-2 border-dashed border-muted-foreground/30 rounded-md p-8 text-center hover:bg-secondary/5 transition-colors cursor-pointer relative group">
                  <input type="file" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" onChange={e => onChange(e.target.files)} {...field} />
                  <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-4 group-hover:text-secondary transition-colors" />
                  <p className="text-sm font-medium text-foreground mb-1">Click to upload payslips or tax returns</p>
                  <p className="text-xs text-muted-foreground">Accepted formats: PDF, PNG, JPG.</p>
                  {value && value.length > 0 && <div className="mt-4 p-2 bg-secondary/10 text-secondary text-sm rounded-md inline-block">
                      {value[0].name} attached
                    </div>}
                </div>
              </FormControl>
              <FormDescription className="text-xs">Uploading documents now can speed up your pre-approval process.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-3 relative z-40">
          <CircleAlert className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <div className="relative z-40 pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full h-16 text-lg font-bold uppercase tracking-wider bg-gradient-to-r from-secondary to-secondary/80 hover:from-secondary/90 hover:to-secondary text-secondary-foreground shadow-lg transition-all hover:shadow-xl rounded-md">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-3 w-6 h-6" />
              Securing Your Rates...
            </> : 'Submit Mortgage Application'}
        </Button>
        <p className="text-center text-xs text-muted-foreground mt-4">
          By submitting, you agree to our privacy policy. Our expert brokers will review your application within 24 business hours.
        </p>
      </div>
    </Form>;
}
export default function StartYourApplication() {
  return <section data-section-id="240783" id="start-your-application" className="bg-background py-24 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:32px_32px]" />
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-secondary/5 rounded-lg blur-3xl -translate-y-1/2" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-primary/5 rounded-lg blur-3xl translate-y-1/2" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-default font-bold text-foreground mb-4 tracking-tight">
            Start Your Application
          </h2>
          <p className="text-lg text-muted-foreground font-default">
            Take the first step toward your new home. Complete our secure application form below, and our Premier Home Loans specialists will craft a tailored mortgage strategy for you.
          </p>
        </div>

        <FormProvider formKey="application_form_contact" formId="application_form_contact" sectionName="Get A Quote" defaultValues={{
        name: "",
        email: "",
        phone: "",
        product_type: "",
        quantity: 720,
        description: "",
        artwork: null
      }} submitText="Submit Mortgage Application" successMessage="Your application has been successfully received. A Premier Home Loans advisor will contact you shortly to discuss the next steps in your home ownership journey.">
          <QuoteFormContent />
        </FormProvider>
      </div>
    </section>;
}